// src/onboarding/ui/PersonalizationBadge.jsx
//
// The status next to a page title (decision 9) — its mounts are listed in onboardingModel's
// BADGE_PATH_PREFIXES, which must stay in sync with them. It describes
// the ACTIVE workspace and renders NOTHING when the store does not (not an admin of it, a
// superadmin, no read yet) — which is also why the suites that render its hosts need no mock.
// A link to the wizard, except where the workspace can never be personalized.
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useOnboarding } from '../onboardingStore.js';
import { badgeKind, stageStep, STAGE_TOTAL, PERSONALIZATION_PATH } from '../onboardingModel.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const TONE = {
  unavailable: SEMANTIC.neutro,
  notPersonalized: SEMANTIC.attenzione,
  failed: SEMANTIC.attenzione,
  stale: SEMANTIC.attenzione,
  running: SEMANTIC.info,
  completed: SEMANTIC.verde,
  // v192 (D2): still personalized, the newest re-run failed.
  completedLastFailed: SEMANTIC.attenzione,
  notAvailable: SEMANTIC.neutro,
};

// No link where the wizard has nothing to offer: the state is unknown, or the workspace can never be personalized.
const PLAIN_KINDS = ['notAvailable', 'unavailable'];

function runningText(t, stage) {
  const step = stageStep(stage);
  if (step === 0) return t('badge.runningStarting');
  if (step == null) return t('badge.running');
  return t('badge.runningStep', { step, total: STAGE_TOTAL });
}

export default function PersonalizationBadge() {
  const { t } = useTranslation('onboarding');
  const { activeTenant } = useAuth();
  const snapshot = useOnboarding();
  const kind = badgeKind(snapshot, activeTenant?.tenant_id || null);
  if (!kind) return null;

  const sem = TONE[kind] || SEMANTIC.neutro;
  const text = kind === 'running'
    ? runningText(t, snapshot.status.personalization?.status)
    : t(`badge.${kind}`);
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px',
    borderRadius: RADII.pill, background: sem.bg, color: sem.fg, fontFamily: FONT_SANS,
    fontSize: '13px', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
  };
  const content = (
    <>
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: RADII.pill, background: sem.fg }} />
      <span style={{ color: COLORS.textSecondary, fontWeight: 400 }}>{t('badge.label')}:</span>
      {text}
    </>
  );
  if (PLAIN_KINDS.includes(kind)) {
    return <span data-testid="personalization-badge" style={style}>{content}</span>;
  }
  return <Link to={PERSONALIZATION_PATH} data-testid="personalization-badge" style={style}>{content}</Link>;
}
